import {useState, useEffect} from "react";
import {Button, Form, Table, Card} from "react-bootstrap";
import axios from "./axiosConfig";
import "./UserPanel.css";

interface Permission {
    id: number;
    name: string;
    description: string;
    user_type: string[]; // Update type to array
}

export default function PermissionsPage() {
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [newPermissionName, setNewPermissionName] = useState<string>("");
    const [newPermissionDescription, setNewPermissionDescription] = useState<string>("");
    const [newUserTypes, setNewUserTypes] = useState<string[]>(["platform_admin"]); // Default selected
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get("/api/user/permissions");
            console.log("Fetched permissions:", response.data);
            setPermissions(response.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setError("Failed to fetch permissions");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!newPermissionName.trim()) {
            setError("Permission name cannot be empty");
            return;
        }
        if (newUserTypes.length === 0) {
            setError("At least one user type must be selected");
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.post("/api/user/permissions", {
                name: newPermissionName,
                description: newPermissionDescription,
                user_types: newUserTypes // Send array of user types
            });
            if (response.status === 200) {
                setPermissions(prevPermissions => [...prevPermissions, response.data]);
                setNewPermissionName("");
                setNewPermissionDescription("");
                setNewUserTypes(["platform_admin"]); // Reset to default selection
            } else {
                throw new Error('Unexpected response');
            }
        } catch (error) {
            // @ts-ignore
            setError("Failed to create permission: " + (error.response?.data.error || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleUserTypeChange = (event: React.ChangeEvent<any>) => {
        const target = event.target as HTMLSelectElement; // Explicitly cast
        const selectedOptions = Array.from(target.selectedOptions, option => option.value);
        setNewUserTypes(selectedOptions);
    };


    return (
        <div className="container mt-4">
            <h1 className="mb-4">Manage Permissions</h1>
            {error && <div className="alert alert-danger">{error}</div>}
            <Card>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Permission Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter permission name"
                                value={newPermissionName}
                                onChange={(e) => setNewPermissionName(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter permission description"
                                value={newPermissionDescription}
                                onChange={(e) => setNewPermissionDescription(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>User Type</Form.Label>
                            <Form.Control as="select" multiple value={newUserTypes} onChange={handleUserTypeChange}>
                                <option value="platform_admin">Platform Admin</option>
                                <option value="tenant_admin">Tenant Admin</option>
                                <option value="tenant">Tenant</option>
                            </Form.Control>
                        </Form.Group>

                        <Button variant="primary" type="submit" disabled={isLoading}>
                            {isLoading ? 'Adding...' : 'Add Permission'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
            {isLoading ? <p>Loading permissions...</p> : (
                <Table striped bordered hover size="sm" responsive variant="dark">
                    <thead>
                    <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Description</th>
                    </tr>
                    </thead>
                    <tbody>
                    {permissions.map((perm, index) => (
                        <tr key={perm.id}>
                            <td>{index + 1}</td>
                            <td>{perm.name}</td>
                            <td>{perm.description}</td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
